import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Route, Home, CarFront, Building2, Plane, TrainFront, Bus, Ship, Car, Timer, MapPin, BedDouble, ChevronDown, ChevronUp, Plus, Trash2, Sparkles, AlarmClock, RefreshCw, CheckCircle2, Luggage, ScanLine, Send, Bot, FileText, ExternalLink, BellRing } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { useStepUp } from "../StepUpGuard";
import { ShareDocButton } from "../ShareDoc";

/* My Travel Journey — stage-by-stage checklist & live assistant (Phase 1).
   Design: white cards, Google color bars (blue/red/yellow/green), light shades. */
const GC = ["#4285F4", "#EA4335", "#F9AB00", "#34A853"];
const inp = "border border-zinc-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-blue-400 bg-white";
const KIND_ICON = { home: Home, getting_there: CarFront, terminal: Building2, transit: Timer, destination: MapPin, hotel: BedDouble };
const MODE_ICON = { flight: Plane, train: TrainFront, bus: Bus, ferry: Ship, car: Car };
const METHODS = [["", "How are you getting there?"], ["taxi", "Taxi"], ["rideshare", "Rideshare (Uber/Lyft)"],
  ["friend_family", "Friend / family drop-off"], ["driving", "Driving (own car)"], ["rental_car", "Rental car"],
  ["public_transport", "Public transportation"], ["bus", "Bus"], ["train", "Train"], ["hotel_shuttle", "Hotel shuttle"],
  ["airport_shuttle", "Airport / station shuttle"], ["walking", "Walking"], ["other", "Other"]];
const RISK = { comfortable: ["Comfortable", "#34A853"], tight: ["Tight", "#F9A825"], high_risk: ["High Risk", "#EA4335"] };
const HOME_BAG_STEPS = ["packed", "loaded", "checked"];

const fmtCountdown = (m) => {
  if (m == null) return null;
  const neg = m < 0, a = Math.abs(m);
  const h = Math.floor(a / 60), mm = a % 60;
  return `${neg ? "-" : ""}${h > 0 ? `${h} hr ` : ""}${mm} min`;
};

export default function JourneySection() {
  const [trips, setTrips] = useState([]);
  const [tripId, setTripId] = useState(() => localStorage.getItem("tp_journey_trip") || "");
  const [journey, setJourney] = useState(null);
  const [next, setNext] = useState(null);
  const [intl, setIntl] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bags, setBags] = useState([]);
  const [passes, setPasses] = useState([]);

  const loadPasses = useCallback((tid) => {
    if (!tid) { setPasses([]); return; }
    api.get(`/journey/${tid}/boarding-passes`).then((r) => setPasses(r.data.items || [])).catch(() => setPasses([]));
  }, []);

  useEffect(() => {
    api.get("/trips").then((r) => setTrips((r.data.items || []).filter((t) => (t.segments || []).length > 0)))
      .catch(() => setTrips([]));
    api.get("/bags").then((r) => setBags(r.data.items || [])).catch(() => {});
  }, []);

  const loadJourney = useCallback((tid) => {
    if (!tid) { setJourney(null); setNext(null); return; }
    api.get(`/journey/${tid}`).then((r) => { setJourney(r.data); setIntl(!!r.data.international); }).catch(() => setJourney(null));
    api.get(`/journey/${tid}/next`).then((r) => setNext(r.data)).catch(() => setNext(null));
  }, []);
  useEffect(() => { loadJourney(tripId); loadPasses(tripId); }, [tripId, loadJourney, loadPasses]);
  useEffect(() => {
    if (!tripId || !journey) return undefined;
    const t = setInterval(() => api.get(`/journey/${tripId}/next`).then((r) => setNext(r.data)).catch(() => {}), 60000);
    return () => clearInterval(t);
  }, [tripId, journey]);

  const generate = async () => {
    if (!tripId) { toast.error("Pick a trip first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/journey/${tripId}/generate`, { international: intl }, { timeout: 60000 });
      setJourney(r.data);
      api.get(`/journey/${tripId}/next`).then((x) => setNext(x.data)).catch(() => {});
      toast.success("Journey checklist ready — personalized to your itinerary");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not build the journey"); }
    setBusy(false);
  };

  const refreshNext = () => api.get(`/journey/${tripId}/next`).then((r) => setNext(r.data)).catch(() => {});

  const toggleLiveAlerts = async () => {
    const enabled = !journey?.live_alerts;
    setJourney((j) => ({ ...j, live_alerts: enabled }));
    try {
      await api.put(`/journey/${tripId}/live-alerts`, { enabled });
      toast.success(enabled
        ? "Live gate alerts ON — we'll watch your flights for gate/terminal changes, delays and connection risk"
        : "Live gate alerts turned off");
    } catch (e) {
      setJourney((j) => ({ ...j, live_alerts: !enabled }));
      toast.error(e?.response?.data?.detail || "Could not update live alerts");
    }
  };

  const toggleItem = async (sid, iid, done) => {
    setJourney((j) => ({ ...j, stages: j.stages.map((s) => s.id !== sid ? s : { ...s, items: s.items.map((i) => i.id === iid ? { ...i, done } : i) }) }));
    try { await api.put(`/journey/${tripId}/stage/${sid}/items/${iid}`, { done }); refreshNext(); }
    catch { loadJourney(tripId); }
  };

  const trip = trips.find((t) => t.id === tripId);

  return (
    <div data-testid="journey-section" className="mb-12">
      <div className="border-t-4 rounded-t" style={{ borderColor: "#4285F4" }} />
      <div className="bg-white border border-zinc-200 border-t-0 p-5">
        <div className="flex flex-wrap items-center gap-3 mb-1">
          <h2 className="font-heading font-900 text-2xl flex items-center gap-2" style={{ color: "#4285F4" }}>
            <Route className="w-6 h-6" /> My Travel Journey
          </h2>
          <span className="text-[11px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "#E8F0FE", color: "#4285F4" }}>Live assistant</span>
          <Link to="/memoirs" data-testid="journey-why-diaries-link"
            className="text-sm font-700 text-indigo-700 hover:underline inline-flex items-center gap-1">
            Why Keep Travel Diaries and Memoirs? →
          </Link>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Step-by-step guidance from home to hotel — checklists for every stage of your trip, built from your itinerary.</p>

        <div className="flex flex-wrap items-center gap-2 mb-5">
          <select value={tripId} onChange={(e) => { setTripId(e.target.value); localStorage.setItem("tp_journey_trip", e.target.value); }}
            data-testid="journey-trip-select" className={`${inp} min-w-[220px]`}>
            <option value="">Choose a trip with itinerary segments…</option>
            {trips.map((t) => <option key={t.id} value={t.id}>{t.title || "Trip"}{t.start_date ? ` — ${t.start_date}` : ""}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer" data-testid="journey-intl-toggle">
            <input type="checkbox" checked={intl} onChange={(e) => setIntl(e.target.checked)} /> International trip
          </label>
          <button onClick={generate} disabled={busy || !tripId} data-testid="journey-generate"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-white text-sm font-600 disabled:opacity-50 hover:opacity-90 transition-opacity"
            style={{ background: "#4285F4" }}>
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {journey ? "Regenerate" : "Build my journey"}
          </button>
          {journey && (
            <button onClick={toggleLiveAlerts} data-testid="journey-live-alerts-toggle"
              aria-pressed={!!journey.live_alerts}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-600 border transition-colors ${journey.live_alerts
                ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                : "border-zinc-300 text-zinc-600 hover:border-emerald-400 hover:text-emerald-700"}`}
              title="Watch flights within 36h for gate/terminal changes, delays and connection risk">
              <BellRing className={`w-4 h-4 ${journey.live_alerts ? "animate-pulse" : ""}`} />
              Live gate alerts {journey.live_alerts ? "ON" : "OFF"}
            </button>
          )}
        </div>

        {!tripId && trips.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="journey-empty">
            Add a trip with flights, trains or other segments first — then build your journey checklist here.{" "}
            <a href="#hub-trips" data-testid="journey-empty-trips-link" className="inline-flex items-center gap-1 font-700 text-primary hover:underline">
              Open My Trips <ExternalLink className="w-3.5 h-3.5" />
            </a>{" "}
            to create or open a trip and add its segments.
          </p>
        )}

        {journey && next && <NextAction next={next} />}

        {journey && (
          <div className="mt-5 relative" data-testid="journey-timeline">
            <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-zinc-200" />
            <div className="space-y-3">
              {journey.stages.map((s, i) => (
                <StageCard key={s.id} s={s} color={GC[i % 4]} tripId={tripId} bags={bags}
                  bagStatus={journey.bag_status || {}} onToggle={toggleItem}
                  passes={passes} onPassesChange={() => { loadPasses(tripId); loadJourney(tripId); }}
                  onReload={() => loadJourney(tripId)} />
              ))}
            </div>
          </div>
        )}
        {journey && <JourneyCompanion tripId={tripId} />}
        {journey?.kids && <p className="text-xs text-muted-foreground mt-3">Family mode: children detected on this trip — kid items included.</p>}
        {trip && !journey && tripId && (
          <p className="text-sm text-muted-foreground" data-testid="journey-none">No journey yet for <b>{trip.title}</b> — click "Build my journey" to generate the stage-by-stage checklist.</p>
        )}
      </div>
    </div>
  );
}

function NextAction({ next }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  if (next.done) {
    return (
      <div className="border border-zinc-200 bg-white p-4 flex items-center gap-3" data-testid="journey-next-done">
        <CheckCircle2 className="w-8 h-8" style={{ color: "#34A853" }} />
        <div><div className="font-heading font-900" style={{ color: "#34A853" }}>Journey complete</div>
          <div className="text-sm text-muted-foreground">{next.message}</div></div>
      </div>
    );
  }
  let mins = next.countdown_minutes;
  if (next.anchor) {
    try { mins = Math.round((new Date(next.anchor.replace(" ", "T")) - new Date()) / 60000); } catch { /* keep server value */ }
  }
  const color = next.urgency === "red" ? "#EA4335" : next.urgency === "yellow" ? "#F9A825" : "#34A853";
  return (
    <div className="border border-zinc-200 bg-white" data-testid="journey-next">
      <div className="h-1.5" style={{ background: color }} />
      <div className="p-4 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">What should I do now?</div>
          <div className="font-heading font-900 text-xl mt-0.5" data-testid="journey-next-action">{next.action || next.stage_title}</div>
          <div className="text-sm text-muted-foreground">{next.stage_title}</div>
        </div>
        {mins != null && (
          <div className="text-right">
            <div className="font-heading font-900 text-2xl tabular-nums" style={{ color }} data-testid="journey-countdown">{fmtCountdown(mins)}</div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">until next departure</div>
          </div>
        )}
      </div>
    </div>
  );
}

function StageCard({ s, color, tripId, bags, bagStatus, onToggle, passes, onPassesChange, onReload }) {
  const [open, setOpen] = useState(s.status === "active");
  const [newItem, setNewItem] = useState("");
  const [alertAt, setAlertAt] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const Icon = s.kind === "leg" ? (MODE_ICON[s.mode] || Plane) : (KIND_ICON[s.kind] || MapPin);
  const items = s.items || [];
  const done = items.filter((i) => i.done).length;
  const showBags = (s.kind === "home" || (s.kind === "destination" && s.mode === "flight")) && bags.length > 0;

  const addItem = async () => {
    if (!newItem.trim()) return;
    try { await api.post(`/journey/${tripId}/stage/${s.id}/items`, { text: newItem }); setNewItem(""); onReload(); }
    catch { toast.error("Could not add item"); }
  };
  const delItem = async (iid) => { await api.delete(`/journey/${tripId}/stage/${s.id}/items/${iid}`); onReload(); };
  const setStatus = async (status) => { await api.put(`/journey/${tripId}/stage/${s.id}`, { status }); onReload(); };
  const setAlert = async () => {
    if (!alertAt) { toast.error("Pick a date & time"); return; }
    try {
      await api.post("/reminders", { entity_type: "journey", entity_id: tripId, title: s.title,
        message: `Journey stage: ${s.title}${s.subtitle ? ` — ${s.subtitle}` : ""}`, remind_at: new Date(alertAt).toISOString() });
      toast.success("Alert set — you'll get a notification & email");
      setShowAlert(false); setAlertAt("");
    } catch { toast.error("Could not set alert"); }
  };

  const d = s.details || {};
  return (
    <div className="relative pl-9" data-testid={`journey-stage-${s.kind}-${s.id}`}>
      <div className="absolute left-0 top-3 w-8 h-8 rounded-full flex items-center justify-center border-2 bg-white z-10"
        style={{ borderColor: color, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`bg-white border border-zinc-200 ${s.status === "done" ? "opacity-60" : ""}`} style={{ borderLeft: `4px solid ${color}` }}>
        <button onClick={() => setOpen((o) => !o)} data-testid={`journey-stage-toggle-${s.id}`} className="w-full text-left p-3.5 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-heading font-800">{s.title}</span>
              {s.status === "active" && <span className="text-[10px] font-800 uppercase px-1.5 py-0.5 rounded-full text-white" style={{ background: color }}>Now</span>}
              {s.status === "done" && <CheckCircle2 className="w-4 h-4" style={{ color: "#34A853" }} />}
              {s.connection?.risk && (
                <span className="text-[11px] font-800 px-1.5 py-0.5 rounded-full" data-testid={`journey-risk-${s.id}`}
                  style={{ background: `${RISK[s.connection.risk][1]}1A`, color: RISK[s.connection.risk][1] }}>
                  ● {RISK[s.connection.risk][0]}{s.connection.gap_minutes != null && ` — ${fmtCountdown(s.connection.gap_minutes)} connection`}
                </span>
              )}
            </div>
            {s.subtitle && <div className="text-xs text-muted-foreground truncate">{s.subtitle}</div>}
          </div>
          {items.length > 0 && <span className="text-xs font-mono tabular-nums shrink-0" style={{ color: done === items.length ? "#34A853" : undefined }}>{done}/{items.length}</span>}
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
        </button>

        {open && (
          <div className="px-4 pb-4">
            {s.kind === "leg" && (
              <div className="text-xs font-mono border border-zinc-200 px-2.5 py-1.5 mb-3 flex flex-wrap gap-x-3 gap-y-0.5" style={{ background: `${color}0D` }} data-testid={`journey-leg-details-${s.id}`}>
                {d.carrier && <span>{d.carrier}</span>}{d.date && <span>{d.date}{d.time ? ` ${d.time}` : ""}</span>}
                {d.terminal && <span>Terminal {d.terminal}</span>}{d.gate && <span>Gate {d.gate}</span>}
                {d.seat && <span>Seat {d.seat}</span>}
                {d.arrive_time && <span>arrives {d.arrive_date ? `${d.arrive_date} ` : ""}{d.arrive_time}</span>}
              </div>
            )}
            {s.kind === "transit" && s.connection && (
              <div className="text-xs border border-zinc-200 px-2.5 py-1.5 mb-3 flex flex-wrap gap-x-4" style={{ background: `${color}0D` }}>
                {s.connection.arrive && <span>Arrive: <b>{s.connection.arrive}</b></span>}
                {s.connection.next_dep && <span>Next departure: <b>{s.connection.next_dep}</b>{s.connection.next_ref ? ` (${s.connection.next_ref})` : ""}</span>}
                {s.connection.gap_minutes != null && <span>Connection time: <b>{fmtCountdown(s.connection.gap_minutes)}</b></span>}
              </div>
            )}
            {s.kind === "getting_there" && <TransportForm s={s} tripId={tripId} onSaved={onReload} />}

            {(s.kind === "leg" || s.kind === "terminal" || s.kind === "transit") && (
              <BoardingPassBlock s={s} tripId={tripId} color={color}
                passes={(passes || []).filter((p) => p.stage_id === s.id)}
                onChange={onPassesChange} />
            )}

            <ul className="space-y-1.5">
              {items.map((i) => (
                <li key={i.id} className="flex items-start gap-2 group" data-testid={`journey-item-${i.id}`}>
                  <input type="checkbox" checked={i.done} onChange={(e) => onToggle(s.id, i.id, e.target.checked)}
                    data-testid={`journey-item-check-${i.id}`} className="mt-0.5 accent-current cursor-pointer" style={{ accentColor: color }} />
                  <span className={`text-sm flex-1 ${i.done ? "line-through text-muted-foreground" : "text-zinc-800"}`}>
                    {i.text}
                    {i.ai && <Sparkles className="w-3 h-3 inline ml-1" style={{ color: "#F9A825" }} title="AI-suggested" />}
                  </span>
                  {i.custom && <button onClick={() => delItem(i.id)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
                </li>
              ))}
            </ul>

            {showBags && <BagChecklist stage={s} tripId={tripId} bags={bags} bagStatus={bagStatus} onReload={onReload} />}

            <div className="flex flex-wrap items-center gap-2 mt-3">
              <input value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addItem()}
                placeholder="Add your own task (e.g. Fill water bottle 10:00)" data-testid={`journey-add-item-${s.id}`} className={`${inp} flex-1 min-w-[180px]`} />
              <button onClick={addItem} data-testid={`journey-add-item-btn-${s.id}`} className="p-2 text-white" style={{ background: color }}><Plus className="w-4 h-4" /></button>
              <button onClick={() => setShowAlert((a) => !a)} data-testid={`journey-alert-toggle-${s.id}`}
                className="inline-flex items-center gap-1 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-zinc-500"><AlarmClock className="w-3.5 h-3.5" /> Set alert</button>
              {s.status !== "active" && s.status !== "done" && (
                <button onClick={() => setStatus("active")} data-testid={`journey-stage-activate-${s.id}`} className="text-xs px-2.5 py-1.5 border border-zinc-300 hover:border-zinc-500">I'm here now</button>
              )}
              {s.status !== "done" && (
                <button onClick={() => setStatus("done")} data-testid={`journey-stage-done-${s.id}`} className="text-xs px-2.5 py-1.5 text-white" style={{ background: "#34A853" }}>Stage done</button>
              )}
              {s.status === "done" && (
                <button onClick={() => setStatus("pending")} className="text-xs px-2.5 py-1.5 border border-zinc-300">Reopen</button>
              )}
            </div>
            {showAlert && (
              <div className="flex items-center gap-2 mt-2" data-testid={`journey-alert-form-${s.id}`}>
                <input type="datetime-local" value={alertAt} onChange={(e) => setAlertAt(e.target.value)} className={inp} data-testid={`journey-alert-time-${s.id}`} />
                <button onClick={setAlert} data-testid={`journey-alert-save-${s.id}`} className="px-3 py-1.5 text-white text-xs font-600" style={{ background: "#EA4335" }}>Save alert</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function BoardingPassBlock({ s, tripId, color, passes, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const guard = useStepUp();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    if (s.kind === "leg") fd.append("stage_id", s.id);
    try {
      const r = await api.post(`/journey/${tripId}/boarding-pass`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      const x = r.data.extracted || {};
      const bits = [x.airline, x.flight_number, x.seat && `Seat ${x.seat}`, x.gate && `Gate ${x.gate}`,
        x.boarding_time && `Boarding ${x.boarding_time}`].filter(Boolean).join(" · ");
      toast.success(bits ? `Boarding pass scanned — ${bits}` : "Boarding pass saved");
      onChange();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not scan the boarding pass"); }
    setBusy(false);
    e.target.value = "";
  };
  const remove = async (bid) => { await api.delete(`/journey/${tripId}/boarding-pass/${bid}`); onChange(); };

  return (
    <div className="mb-3" data-testid={`journey-bp-${s.id}`}>
      {passes.map((p) => {
        const x = p.extracted || {};
        return (
          <div key={p.id} className="border border-zinc-200 px-2.5 py-1.5 mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
            style={{ background: `${color}0D` }} data-testid={`journey-bp-card-${p.id}`}>
            <FileText className="w-3.5 h-3.5 shrink-0" style={{ color }} />
            <span className="font-700">{[x.airline, x.flight_number].filter(Boolean).join(" ") || "Boarding pass"}</span>
            {x.passenger && <span>{x.passenger}</span>}
            {(x.origin || x.destination) && <span className="font-mono">{x.origin || "?"} → {x.destination || "?"}</span>}
            {x.seat && <span>Seat <b>{x.seat}</b></span>}
            {x.gate && <span>Gate <b>{x.gate}</b></span>}
            {x.boarding_time && <span>Boarding <b>{x.boarding_time}</b></span>}
            {x.boarding_group && <span>Group <b>{x.boarding_group}</b></span>}
            <button onClick={() => guard(() => window.open(`${API}/journey/${tripId}/boarding-pass/${p.id}/file?auth=${localStorage.getItem("token")}`, "_blank", "noreferrer"))}
              className="inline-flex items-center gap-1 hover:underline" style={{ color }} data-testid={`journey-bp-view-${p.id}`}>
              View <ExternalLink className="w-3 h-3" /></button>
            <ShareDocButton docType="boarding_pass" refIds={{ pass_id: p.id }} label={[x.airline, x.flight_number].filter(Boolean).join(" ") || "Boarding pass"} />
            <button onClick={() => remove(p.id)} data-testid={`journey-bp-delete-${p.id}`} className="ml-auto text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden"
        onChange={upload} data-testid={`journey-bp-file-${s.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`journey-bp-upload-${s.id}`}
        className="inline-flex items-center gap-1.5 border border-zinc-300 px-2.5 py-1.5 text-xs hover:border-zinc-500 disabled:opacity-60">
        {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" style={{ color }} />}
        {busy ? "Scanning…" : "Upload boarding pass (photo or PDF) — auto-fills gate, seat & times"}
      </button>
    </div>
  );
}

const QUICK_QS = ["What should I do now?", "How much time do I have?", "Where is my gate?", "What haven't I done yet?"];

function JourneyCompanion({ tripId }) {
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => {
    api.get(`/journey/${tripId}/companion`).then((r) => setMsgs(r.data.items || [])).catch(() => setMsgs([]));
  }, [tripId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    setQ("");
    setMsgs((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", text }]);
    setBusy(true);
    try {
      const r = await api.post(`/journey/${tripId}/companion`, { question: text }, { timeout: 60000 });
      setMsgs((m) => [...m, { id: `a-${Date.now()}`, role: "assistant", text: r.data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Companion unavailable — try again");
      setMsgs((m) => m.filter((x) => !String(x.id).startsWith("tmp-")));
    }
    setBusy(false);
  };

  return (
    <div className="mt-6 border border-zinc-200 bg-white" data-testid="journey-companion">
      <div className="h-1.5 flex">
        {GC.map((c) => <div key={c} className="flex-1" style={{ background: c }} />)}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5" style={{ color: "#4285F4" }} />
          <span className="font-heading font-900">Travel Companion</span>
          <span className="text-[11px] font-700 uppercase tracking-wide px-2 py-0.5 rounded-full" style={{ background: "#E8F0FE", color: "#4285F4" }}>AI</span>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Ask anything about this journey — answers come from your real itinerary, gates, times and checklists.</p>

        {msgs.length > 0 && (
          <div className="max-h-72 overflow-y-auto space-y-2 mb-3 pr-1" data-testid="journey-companion-messages">
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-line ${m.role === "user" ? "text-white" : "border border-zinc-200 text-zinc-800"}`}
                  style={m.role === "user" ? { background: "#4285F4" } : { background: "#F8F9FA" }}
                  data-testid={`journey-companion-msg-${m.role}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><RefreshCw className="w-3 h-3 animate-spin" /> thinking…</div>}
            <div ref={endRef} />
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mb-2">
          {QUICK_QS.map((s) => (
            <button key={s} onClick={() => ask(s)} disabled={busy} data-testid={`journey-companion-quick-${s.split(" ")[1]}`}
              className="text-xs border border-zinc-300 px-2 py-1 rounded-full hover:border-zinc-500 disabled:opacity-50">{s}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
            placeholder='e.g. "Do I have enough time to eat?"' data-testid="journey-companion-input"
            className={`${inp} flex-1`} disabled={busy} />
          <button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="journey-companion-send"
            className="p-2 text-white disabled:opacity-50" style={{ background: "#4285F4" }}><Send className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
}

function TransportForm({ s, tripId, onSaved }) {
  const [tr, setTr] = useState(s.transport || { method: "" });
  const save = async () => {
    try { await api.put(`/journey/${tripId}/stage/${s.id}`, { transport: tr }); toast.success("Transport plan saved"); onSaved(); }
    catch { toast.error("Could not save"); }
  };
  const set = (k, v) => setTr((x) => ({ ...x, [k]: v }));
  const m = tr.method;
  return (
    <div className="border border-zinc-200 p-3 mb-3" style={{ background: "#4285F40D" }} data-testid={`journey-transport-${s.id}`}>
      <div className="grid sm:grid-cols-2 gap-2">
        <select value={m || ""} onChange={(e) => set("method", e.target.value)} data-testid={`journey-transport-method-${s.id}`} className={inp}>
          {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {(m === "driving" || m === "rental_car") && <>
          <input value={tr.vehicle || ""} onChange={(e) => set("vehicle", e.target.value)} placeholder="Vehicle" className={inp} />
          <input value={tr.parking_facility || ""} onChange={(e) => set("parking_facility", e.target.value)} placeholder="Parking facility" data-testid={`journey-parking-${s.id}`} className={inp} />
          <input value={tr.parking_location || ""} onChange={(e) => set("parking_location", e.target.value)} placeholder="Parking location / lot" className={inp} />
          <input value={tr.parking_level || ""} onChange={(e) => set("parking_level", e.target.value)} placeholder="Level" className={inp} />
          <input value={tr.parking_space || ""} onChange={(e) => set("parking_space", e.target.value)} placeholder="Space #" className={inp} />
          <input value={tr.reservation_number || ""} onChange={(e) => set("reservation_number", e.target.value)} placeholder="Reservation #" className={inp} />
          <input value={tr.cost || ""} onChange={(e) => set("cost", e.target.value)} placeholder="Cost" className={inp} />
          <input value={tr.shuttle_info || ""} onChange={(e) => set("shuttle_info", e.target.value)} placeholder="Shuttle info" className={inp} />
          <input value={tr.walk_minutes || ""} onChange={(e) => set("walk_minutes", e.target.value)} placeholder="Walk to terminal (min)" className={inp} />
        </>}
        {(m === "rideshare" || m === "taxi") && <>
          <input value={tr.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="Company (Uber, Lyft…)" data-testid={`journey-rideshare-company-${s.id}`} className={inp} />
          <input value={tr.pickup_location || ""} onChange={(e) => set("pickup_location", e.target.value)} placeholder="Pickup location" className={inp} />
          <input value={tr.pickup_time || ""} onChange={(e) => set("pickup_time", e.target.value)} placeholder="Estimated pickup time" className={inp} />
          <input value={tr.driver_info || ""} onChange={(e) => set("driver_info", e.target.value)} placeholder="Driver info (when available)" className={inp} />
        </>}
        {m && !["driving", "rental_car", "rideshare", "taxi", ""].includes(m) && (
          <input value={tr.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Details (route, pickup point, times…)" className={`${inp} sm:col-span-1`} />
        )}
      </div>
      <div className="flex justify-end mt-2">
        <button onClick={save} data-testid={`journey-transport-save-${s.id}`} className="px-3 py-1.5 text-white text-xs font-600" style={{ background: "#4285F4" }}>Save transport plan</button>
      </div>
    </div>
  );
}

function BagChecklist({ stage, tripId, bags, bagStatus, onReload }) {
  const isHome = stage.kind === "home";
  const cycle = async (bag) => {
    const cur = bagStatus[bag.id] || "";
    let nextS;
    if (isHome) {
      const i = HOME_BAG_STEPS.indexOf(cur);
      nextS = i === HOME_BAG_STEPS.length - 1 ? "" : HOME_BAG_STEPS[i + 1];
    } else nextS = cur === "collected" ? "" : "collected";
    await api.put(`/journey/${tripId}/bags/${bag.id}`, { status: nextS });
    onReload();
  };
  return (
    <div className="mt-3 border border-zinc-200 p-3" style={{ background: "#34A8530D" }} data-testid={`journey-bags-${stage.id}`}>
      <div className="text-xs font-700 flex items-center gap-1.5 mb-2" style={{ color: "#34A853" }}>
        <Luggage className="w-3.5 h-3.5" /> {isHome ? "Your luggage — tap to mark Packed → Loaded → Checked" : "Baggage claim — verify every bag (tap to mark collected)"}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {bags.map((b) => {
          const st = bagStatus[b.id] || "";
          const active = isHome ? !!st : st === "collected";
          return (
            <button key={b.id} onClick={() => cycle(b)} data-testid={`journey-bag-${b.id}`}
              className={`text-xs font-600 px-2 py-1 rounded-full border transition-colors ${active ? "text-white" : "border-zinc-300 text-zinc-600 hover:border-zinc-500"}`}
              style={active ? { background: "#34A853", borderColor: "#34A853" } : {}}>
              {active && "✓ "}{b.name}{st && ` — ${st}`}
            </button>
          );
        })}
      </div>
    </div>
  );
}
